import { useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectIsAdmin,
  selectClinicSharesAllPatients,
} from "../../store/slices/authSlice";
import {
  useGetPatientFilesQuery,
  useGetPatientFoldersQuery,
  useUploadPatientFileMutation,
  useUpdatePatientFileMutation,
  useDeletePatientFileMutation,
  useCreatePatientFolderMutation,
  useUpdatePatientFolderMutation,
  useDeletePatientFolderMutation,
  useGetStorageUsageQuery,
} from "../../store/services/patientFilesApi";
import {
  MAX_FILE_SIZE_MB,
  ALLOWED_PATIENT_FILE_ACCEPT,
  isAllowedPatientFileType,
  getFileTypeCategory,
  isFilePreviewable,
} from "../../firebase";
import {
  Folder,
  FolderOpen,
  File,
  FilePdf,
  FileDoc,
  FileXls,
  Image,
  UploadSimple,
  Plus,
  Trash,
  PencilSimple,
  X,
  Check,
  Eye,
  DownloadSimple,
  CaretLeft,
  CloudArrowUp,
  FolderPlus,
  Warning,
  Info,
  Camera,
  ImageSquare,
} from "@phosphor-icons/react";
import { HelpButton } from "../shared";
import ModalCloseButton from "../ui/ModalCloseButton";
import EmptyDraw from "../ui/EmptyDraw";
import "./PatientFilesTab.css";
import ContentLoader from "../shared/ContentLoader";
import LoaderLoop from "../shared/loader/LoaderLoop";

// File type icons
const FILE_ICONS = {
  image: Image,
  pdf: FilePdf,
  word: FileDoc,
  excel: FileXls,
  other: File,
};

// Folder colors
const FOLDER_COLORS = [
  "#6366f1", // Indigo
  "#8b5cf6", // Purple
  "#ec4899", // Pink
  "#ef4444", // Red
  "#f97316", // Orange
  "#eab308", // Yellow
  "#22c55e", // Green
  "#14b8a6", // Teal
  "#3b82f6", // Blue
  "#6b7280", // Gray
];

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

function PatientFilesTab({ patientId, canEdit = true }) {
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const isAdmin = useSelector(selectIsAdmin);
  const sharesAllPatients = useSelector(selectClinicSharesAllPatients);
  // Admins and "all" sharing-mode members list every file of the patient
  // (rules allow the read). Legacy/assigned clinics keep the uploader filter.
  const listAllUploaders = isAdmin || sharesAllPatients;

  // State
  const [selectedFolder, setSelectedFolder] = useState(null); // null = root
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [showFilePreview, setShowFilePreview] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [folderForm, setFolderForm] = useState({ name: "", color: FOLDER_COLORS[0] });
  const [fileDescriptionForm, setFileDescriptionForm] = useState("");

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Check if mobile device
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Queries
  const { data: files = [], isLoading: isLoadingFiles } = useGetPatientFilesQuery(
    {
      clinicId,
      patientId,
      therapistId: listAllUploaders ? null : therapistId,
      folderId: selectedFolder,
    },
    { skip: !clinicId || !patientId || !therapistId }
  );

  const { data: folders = [], isLoading: isLoadingFolders } = useGetPatientFoldersQuery(
    { clinicId, patientId },
    { skip: !clinicId || !patientId }
  );

  const { data: storageUsage } = useGetStorageUsageQuery(
    { clinicId },
    { skip: !clinicId }
  );

  // Mutations
  const [uploadFile, { isLoading: isUploading }] = useUploadPatientFileMutation();
  const [updateFile] = useUpdatePatientFileMutation();
  const [deleteFile, { isLoading: isDeleting }] = useDeletePatientFileMutation();
  const [createFolder, { isLoading: isCreatingFolder }] = useCreatePatientFolderMutation();
  const [updateFolder] = useUpdatePatientFolderMutation();
  const [deleteFolder, { isLoading: isDeletingFolder }] = useDeletePatientFolderMutation();

  // Handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canEdit) return;
    setIsDragging(true);
  }, [canEdit]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (!canEdit) return;

      const droppedFiles = Array.from(e.dataTransfer.files);
      await handleFilesUpload(droppedFiles);
    },
    [canEdit]
  );

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    await handleFilesUpload(selectedFiles);
    // Reset all inputs
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  };

  const handleFilesUpload = async (filesToUpload) => {
    setUploadError(null);

    for (const file of filesToUpload) {
      // Check file size
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setUploadError(`הקובץ ${file.name} גדול מדי. הגודל המקסימלי הוא ${MAX_FILE_SIZE_MB}MB`);
        continue;
      }
      // Check file type (same allowlist as storage.rules — fail early, in Hebrew)
      if (!isAllowedPatientFileType(file)) {
        setUploadError(
          `סוג הקובץ של ${file.name} אינו נתמך. ניתן להעלות תמונות, PDF, Word, Excel וקבצי טקסט.`
        );
        continue;
      }

      try {
        await uploadFile({
          file,
          clinicId,
          patientId,
          therapistId,
          therapistName,
          folderId: selectedFolder,
        }).unwrap();
      } catch (error) {
        console.error("[CK-2300] Error uploading file:", {
          errorCode: "CK-2300",
          errorMessage: error.message,
          fileName: file.name,
          timestamp: new Date().toISOString(),
        });
        const isStorageDenied =
          error?.code === "storage/unauthorized" ||
          /storage\/unauthorized|unauthorized|permission/i.test(error?.message || "");
        setUploadError(
          isStorageDenied
            ? "אין הרשאה להעלות קובץ זה. בדוק את סוג הקובץ או פנה למנהל הקליניקה."
            : error.message || "שגיאה בהעלאת הקובץ"
        );
      }
    }
  };

  const handleCreateFolder = async () => {
    if (!folderForm.name.trim()) return;

    try {
      await createFolder({
        clinicId,
        patientId,
        folderData: {
          name: folderForm.name.trim(),
          color: folderForm.color,
        },
        therapistId,
        therapistName,
      }).unwrap();

      setShowFolderModal(false);
      setFolderForm({ name: "", color: FOLDER_COLORS[0] });
    } catch (error) {
      console.error("[CK-2301] Error creating folder:", {
        errorCode: "CK-2301",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleUpdateFolder = async () => {
    if (!editingFolder || !folderForm.name.trim()) return;

    try {
      await updateFolder({
        clinicId,
        patientId,
        folderId: editingFolder.id,
        folderData: {
          name: folderForm.name.trim(),
          color: folderForm.color,
        },
      }).unwrap();

      setEditingFolder(null);
      setFolderForm({ name: "", color: FOLDER_COLORS[0] });
    } catch (error) {
      console.error("[CK-2302] Error updating folder:", {
        errorCode: "CK-2302",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await deleteFolder({ clinicId, patientId, folderId }).unwrap();
      setShowDeleteConfirm(null);
      if (selectedFolder === folderId) {
        setSelectedFolder(null);
      }
    } catch (error) {
      console.error("[CK-2303] Error deleting folder:", {
        errorCode: "CK-2303",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
      alert(error.message || "שגיאה במחיקת התיקייה");
    }
  };

  const handleUpdateFileDescription = async () => {
    if (!editingFile) return;

    try {
      await updateFile({
        clinicId,
        patientId,
        fileId: editingFile.id,
        fileData: {
          description: fileDescriptionForm.trim() || null,
        },
      }).unwrap();

      setEditingFile(null);
      setFileDescriptionForm("");
    } catch (error) {
      console.error("[CK-2304] Error updating file:", {
        errorCode: "CK-2304",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleMoveFileToFolder = async (fileId, folderId) => {
    try {
      await updateFile({
        clinicId,
        patientId,
        fileId,
        fileData: { folderId },
      }).unwrap();
    } catch (error) {
      console.error("[CK-2305] Error moving file:", {
        errorCode: "CK-2305",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await deleteFile({
        clinicId,
        patientId,
        fileId,
        therapistId,
        therapistName,
      }).unwrap();
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error("[CK-2306] Error deleting file:", {
        errorCode: "CK-2306",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleDownload = (file) => {
    window.open(file.fileUrl, "_blank");
  };

  const handlePreview = (file) => {
    if (isFilePreviewable(file.fileType)) {
      setShowFilePreview(file);
    } else {
      // Download if not previewable
      handleDownload(file);
    }
  };

  const openEditFolder = (folder) => {
    setEditingFolder(folder);
    setFolderForm({ name: folder.name, color: folder.color || FOLDER_COLORS[0] });
  };

  const openEditFile = (file) => {
    setEditingFile(file);
    setFileDescriptionForm(file.description || "");
  };

  const FileIcon = ({ fileType, size = 24 }) => {
    const category = getFileTypeCategory(fileType);
    const Icon = FILE_ICONS[category] || File;
    return <Icon size={size} weight="duotone" />;
  };

  // Render storage usage bar
  const renderStorageUsage = () => {
    if (!storageUsage) return null;

    const { usedStorageInMb, allowedStorageInMbMaxPerClinic, percentUsed } = storageUsage;
    const isNearLimit = percentUsed > 80;

    return (
      <div className={`storage-usage-bar ${isNearLimit ? "warning" : ""}`}>
        <div className="storage-info">
          <span className="storage-label">שימוש באחסון</span>
          <span className="storage-values">
            {usedStorageInMb.toFixed(1)} / {allowedStorageInMbMaxPerClinic} MB
          </span>
        </div>
        <div className="storage-progress">
          <div
            className="storage-progress-fill"
            style={{ width: `${Math.min(100, percentUsed)}%` }}
          />
        </div>
        {isNearLimit && (
          <div className="storage-warning">
            <Warning size={14} weight="bold" />
            מתקרב למגבלת האחסון
          </div>
        )}
      </div>
    );
  };

  // Loading state
  if (isLoadingFiles || isLoadingFolders) {
    return (
      <div className="patient-files-tab">
        <ContentLoader fill message="טוען קבצים..." />
      </div>
    );
  }

  return (
    <div className="patient-files-tab">
      {/* Header */}
      <div className="files-header">
        <div className="files-header-title">
          <Folder size={22} weight="duotone" />
          <h3>תיק מטופל</h3>
          <span className="files-count">
            {files.length} קבצים
          </span>
        </div>

        <div className="files-header-right">
          <HelpButton featureKey="patient_files" />
          {canEdit && (
            <div className="files-header-actions">
            <button
              className="files-btn secondary"
              onClick={() => setShowFolderModal(true)}
            >
              <FolderPlus size={18} weight="bold" />
              תיקייה חדשה
            </button>
            
            {/* Camera Button - Mobile only */}
            {isMobile && (
              <button
                className="files-btn primary camera-btn"
                onClick={() => cameraInputRef.current?.click()}
              >
                <Camera size={18} weight="bold" />
                צלם
              </button>
            )}
            
            {/* Gallery Button - Mobile only */}
            {isMobile && (
              <button
                className="files-btn primary gallery-btn"
                onClick={() => galleryInputRef.current?.click()}
              >
                <ImageSquare size={18} weight="bold" />
                גלריה
              </button>
            )}
            
            {/* Regular File Upload */}
            <button
              className="files-btn primary"
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadSimple size={18} weight="bold" />
              {isMobile ? "קובץ" : "העלאת קובץ"}
            </button>
            
            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_PATIENT_FILE_ACCEPT}
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
          </div>
          )}
        </div>
      </div>

      {/* Storage Usage */}
      {renderStorageUsage()}

      {/* Error Message */}
      {uploadError && (
        <div className="files-error">
          <Warning size={18} weight="bold" />
          <span>{uploadError}</span>
          <button onClick={() => setUploadError(null)}>
            <X size={16} weight="bold" />
          </button>
        </div>
      )}

      {/* Folders Section */}
      {folders.length > 0 && (
        <div className="folders-section">
          <div className="folders-header">
            <span>תיקיות</span>
          </div>
          <div className="folders-list">
            {/* Root folder option */}
            <button
              className={`folder-item ${selectedFolder === null ? "active" : ""}`}
              onClick={() => setSelectedFolder(null)}
            >
              <FolderOpen size={20} weight="duotone" />
              <span>כל הקבצים</span>
            </button>

            {folders.map((folder) => (
              <div
                key={folder.id}
                className={`folder-item ${selectedFolder === folder.id ? "active" : ""}`}
              >
                <button
                  className="folder-btn"
                  onClick={() => setSelectedFolder(folder.id)}
                >
                  <Folder
                    size={20}
                    weight="duotone"
                    style={{ color: folder.color || FOLDER_COLORS[0] }}
                  />
                  <span>{folder.name}</span>
                </button>
                {canEdit && (
                  <div className="folder-actions">
                    <button
                      className="folder-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditFolder(folder);
                      }}
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      className="folder-action-btn delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteConfirm({ type: "folder", item: folder });
                      }}
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Breadcrumb when in folder */}
      {selectedFolder && (
        <div className="files-breadcrumb">
          <button onClick={() => setSelectedFolder(null)}>
            <CaretLeft size={14} weight="bold" />
            חזרה לכל הקבצים
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">
            {folders.find((f) => f.id === selectedFolder)?.name || "תיקייה"}
          </span>
        </div>
      )}

      {/* Drop Zone */}
      <div
        className={`files-drop-zone ${isDragging ? "dragging" : ""} ${!canEdit ? "disabled" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isUploading ? (
          <div className="drop-zone-uploading">
            <LoaderLoop size="md" />
            <span>מעלה קבצים...</span>
          </div>
        ) : files.length === 0 && !isDragging ? (
          <div className="drop-zone-empty">
            <EmptyDraw variant="files" />
            <h4>אין קבצים עדיין</h4>
            {canEdit && (
              <p>גרור קבצים לכאן או לחץ על "העלאת קובץ"</p>
            )}
            <span className="drop-zone-hint">
              <Info size={14} />
              גודל מקסימלי: {MAX_FILE_SIZE_MB}MB לקובץ
            </span>
          </div>
        ) : isDragging ? (
          <div className="drop-zone-dragging">
            <CloudArrowUp size={48} weight="duotone" />
            <h4>שחרר כדי להעלות</h4>
          </div>
        ) : (
          <div className="files-grid">
            {files.map((file) => (
              <div key={file.id} className="file-card">
                <div className="file-card-icon">
                  <FileIcon fileType={file.fileType} size={32} />
                </div>

                <div className="file-card-info">
                  <span className="file-name" title={file.fileName}>
                    {file.fileName}
                  </span>
                  {file.description && (
                    <span className="file-description">{file.description}</span>
                  )}
                  <div className="file-meta">
                    <span>{formatFileSize(file.fileSize)}</span>
                    <span className="meta-dot" />
                    <span>{formatDate(file.createdAt)}</span>
                  </div>
                  <div className="file-uploader">
                    העלה: {file.uploadedByName}
                  </div>
                </div>

                <div className="file-card-actions">
                  {isFilePreviewable(file.fileType) && (
                    <button
                      className="file-action-btn"
                      onClick={() => handlePreview(file)}
                      title="תצוגה מקדימה"
                    >
                      <Eye size={16} weight="bold" />
                    </button>
                  )}
                  <button
                    className="file-action-btn"
                    onClick={() => handleDownload(file)}
                    title="הורדה"
                  >
                    <DownloadSimple size={16} weight="bold" />
                  </button>
                  {canEdit && (
                    <>
                      <button
                        className="file-action-btn"
                        onClick={() => openEditFile(file)}
                        title="עריכת תיאור"
                      >
                        <PencilSimple size={16} weight="bold" />
                      </button>
                      <button
                        className="file-action-btn delete"
                        onClick={() => setShowDeleteConfirm({ type: "file", item: file })}
                        title="מחיקה"
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Folder Modal */}
      {(showFolderModal || editingFolder) && (
        <div className="files-modal-overlay" onClick={() => {
          setShowFolderModal(false);
          setEditingFolder(null);
          setFolderForm({ name: "", color: FOLDER_COLORS[0] });
        }}>
          <div className="files-modal" onClick={(e) => e.stopPropagation()}>
            <div className="files-modal-header">
              <h3>{editingFolder ? "עריכת תיקייה" : "תיקייה חדשה"}</h3>
              <ModalCloseButton onClick={() => {
                  setShowFolderModal(false);
                  setEditingFolder(null);
                  setFolderForm({ name: "", color: FOLDER_COLORS[0] });
                }} />
            </div>

            <div className="files-modal-body">
              <div className="form-group">
                <label>שם התיקייה</label>
                <input
                  type="text"
                  value={folderForm.name}
                  onChange={(e) => setFolderForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="לדוגמה: אבחונים"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>צבע</label>
                <div className="color-picker">
                  {FOLDER_COLORS.map((color) => (
                    <button
                      key={color}
                      className={`color-option ${folderForm.color === color ? "active" : ""}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFolderForm((prev) => ({ ...prev, color }))}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="files-modal-footer">
              <button
                className="files-btn secondary"
                onClick={() => {
                  setShowFolderModal(false);
                  setEditingFolder(null);
                  setFolderForm({ name: "", color: FOLDER_COLORS[0] });
                }}
              >
                ביטול
              </button>
              <button
                className="files-btn primary"
                onClick={editingFolder ? handleUpdateFolder : handleCreateFolder}
                disabled={!folderForm.name.trim() || isCreatingFolder}
              >
                {isCreatingFolder ? (
                  <LoaderLoop size="xs" />
                ) : (
                  <Check size={16} weight="bold" />
                )}
                {editingFolder ? "שמור" : "צור תיקייה"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit File Description Modal */}
      {editingFile && (
        <div className="files-modal-overlay" onClick={() => {
          setEditingFile(null);
          setFileDescriptionForm("");
        }}>
          <div className="files-modal" onClick={(e) => e.stopPropagation()}>
            <div className="files-modal-header">
              <h3>עריכת תיאור קובץ</h3>
              <ModalCloseButton onClick={() => {
                  setEditingFile(null);
                  setFileDescriptionForm("");
                }} />
            </div>

            <div className="files-modal-body">
              <div className="file-preview-info">
                <FileIcon fileType={editingFile.fileType} size={24} />
                <span>{editingFile.fileName}</span>
              </div>

              <div className="form-group">
                <label>תיאור (אופציונלי)</label>
                <textarea
                  value={fileDescriptionForm}
                  onChange={(e) => setFileDescriptionForm(e.target.value)}
                  placeholder="לדוגמה: אבחון מפסיכולוג מ-2024"
                  rows={3}
                />
              </div>

              {folders.length > 0 && (
                <div className="form-group">
                  <label>העבר לתיקייה</label>
                  <select
                    value={editingFile.folderId || ""}
                    onChange={(e) => handleMoveFileToFolder(editingFile.id, e.target.value || null)}
                  >
                    <option value="">ללא תיקייה</option>
                    {folders.map((folder) => (
                      <option key={folder.id} value={folder.id}>
                        {folder.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="files-modal-footer">
              <button
                className="files-btn secondary"
                onClick={() => {
                  setEditingFile(null);
                  setFileDescriptionForm("");
                }}
              >
                ביטול
              </button>
              <button
                className="files-btn primary"
                onClick={handleUpdateFileDescription}
              >
                <Check size={16} weight="bold" />
                שמור
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {showFilePreview && (
        <div className="files-modal-overlay preview-overlay" onClick={() => setShowFilePreview(null)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-header">
              <span className="preview-filename">{showFilePreview.fileName}</span>
              <div className="preview-actions">
                <button onClick={() => handleDownload(showFilePreview)}>
                  <DownloadSimple size={20} weight="bold" />
                  הורדה
                </button>
                <ModalCloseButton onClick={() => setShowFilePreview(null)} />
              </div>
            </div>
            <div className="preview-content">
              {showFilePreview.fileType.startsWith("image/") ? (
                <img src={showFilePreview.fileUrl} alt={showFilePreview.fileName} />
              ) : showFilePreview.fileType === "application/pdf" ? (
                <iframe
                  src={showFilePreview.fileUrl}
                  title={showFilePreview.fileName}
                  width="100%"
                  height="100%"
                />
              ) : (
                <div className="preview-not-available">
                  <File size={64} weight="duotone" />
                  <p>תצוגה מקדימה לא זמינה לסוג קובץ זה</p>
                  <button
                    className="files-btn primary"
                    onClick={() => handleDownload(showFilePreview)}
                  >
                    <DownloadSimple size={18} weight="bold" />
                    הורד לצפייה
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="files-modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="files-modal delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="files-modal-header">
              <h3>
                {showDeleteConfirm.type === "folder"
                  ? "מחיקת תיקייה"
                  : "מחיקת קובץ"}
              </h3>
            </div>

            <div className="files-modal-body">
              <div className="delete-warning">
                <Trash size={32} weight="duotone" />
                <p>
                  {showDeleteConfirm.type === "folder"
                    ? `האם אתה בטוח שברצונך למחוק את התיקייה "${showDeleteConfirm.item.name}"?`
                    : `האם אתה בטוח שברצונך למחוק את הקובץ "${showDeleteConfirm.item.fileName}"?`}
                </p>
                {showDeleteConfirm.type === "file" && (
                  <span className="delete-note">פעולה זו לא ניתנת לשחזור</span>
                )}
              </div>
            </div>

            <div className="files-modal-footer">
              <button
                className="files-btn secondary"
                onClick={() => setShowDeleteConfirm(null)}
              >
                ביטול
              </button>
              <button
                className="files-btn danger"
                onClick={() => {
                  if (showDeleteConfirm.type === "folder") {
                    handleDeleteFolder(showDeleteConfirm.item.id);
                  } else {
                    handleDeleteFile(showDeleteConfirm.item.id);
                  }
                }}
                disabled={isDeleting || isDeletingFolder}
              >
                {(isDeleting || isDeletingFolder) ? (
                  <LoaderLoop size="xs" />
                ) : (
                  <Trash size={16} weight="bold" />
                )}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientFilesTab;

